import { formatNumber } from './lottery'

export type ShareTicket = {
  numbers: number[]
  secondary?: number[]
  singleDigit?: boolean
}

export type ShareOptions = {
  gameName: string
  rule: string
  filename: string
  primaryColor?: string
  secondaryColor?: string
}

function drawBall(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  value: number,
  color: string,
  singleDigit = false,
) {
  ctx.save()
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = 'rgba(255,255,255,.38)'
  ctx.lineWidth = 2
  ctx.stroke()
  ctx.restore()

  ctx.fillStyle = '#ffffff'
  ctx.font = `700 ${Math.floor(radius * 0.88)}px ui-sans-serif, system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(singleDigit ? String(value) : formatNumber(value), x, y + 1)
}

function drawTicketBalls(
  ctx: CanvasRenderingContext2D,
  ticket: ShareTicket,
  y: number,
  primaryColor: string,
  secondaryColor: string,
) {
  const primary = ticket.numbers
  const secondary = ticket.secondary ?? []
  const total = primary.length + secondary.length
  const radius = total >= 10 ? 26 : total >= 8 ? 28 : 32
  const left = 180
  const right = 948
  const gapBeforeSecondary = secondary.length ? 24 : 0
  const available = right - left - gapBeforeSecondary
  const steps = Math.max(1, total - 1)
  const spacing = Math.min(112, available / steps)

  primary.forEach((value, index) => {
    drawBall(
      ctx,
      left + index * spacing,
      y - 4,
      radius,
      value,
      primaryColor,
      ticket.singleDigit,
    )
  })

  secondary.forEach((value, index) => {
    const x = left + (primary.length + index) * spacing + gapBeforeSecondary
    drawBall(ctx, x, y - 4, radius, value, secondaryColor, ticket.singleDigit)
  })
}

export function createShareImage(tickets: ShareTicket[], options: ShareOptions): string {
  const shown = tickets.slice(0, 24)
  const width = 1080
  const rowHeight = 96
  const height = Math.max(720, 300 + shown.length * rowHeight + 150)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas unavailable')

  const bg = ctx.createLinearGradient(0, 0, width, height)
  bg.addColorStop(0, '#ffffff')
  bg.addColorStop(0.55, '#fbfbfd')
  bg.addColorStop(1, '#f4f7ff')
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, width, height)

  ctx.fillStyle = 'rgba(79,127,232,.035)'
  for (let i = 0; i < 24; i++) {
    ctx.beginPath()
    ctx.arc((i * 137) % width, (i * 223) % height, 80 + (i % 4) * 28, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.fillStyle = '#172033'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.font = '800 50px ui-sans-serif, system-ui, sans-serif'
  ctx.fillText('回归线福彩俱乐部', 72, 96)

  ctx.fillStyle = '#ce3441'
  ctx.font = '800 30px ui-sans-serif, system-ui, sans-serif'
  ctx.fillText(options.gameName, 72, 145)

  ctx.fillStyle = '#7a8496'
  ctx.font = '24px ui-sans-serif, system-ui, sans-serif'
  ctx.fillText(options.rule, 72, 184)

  ctx.fillStyle = '#344054'
  ctx.font = '600 28px ui-sans-serif, system-ui, sans-serif'
  ctx.fillText(`本次生成 ${tickets.length} 注`, 72, 230)

  const primaryColor = options.primaryColor ?? '#e95662'
  const secondaryColor = options.secondaryColor ?? '#4f7fe8'
  const startY = 282

  shown.forEach((ticket, index) => {
    const y = startY + index * rowHeight
    ctx.fillStyle = '#ffffff'
    ctx.beginPath()
    ctx.roundRect(52, y - 42, 976, 78, 24)
    ctx.fill()
    ctx.strokeStyle = '#eaecf0'
    ctx.lineWidth = 2
    ctx.stroke()

    ctx.fillStyle = '#7a8496'
    ctx.font = '700 22px ui-monospace, SFMono-Regular, monospace'
    ctx.textAlign = 'left'
    ctx.fillText(String(index + 1).padStart(2, '0'), 78, y + 8)

    drawTicketBalls(ctx, ticket, y, primaryColor, secondaryColor)
  })

  const footerY = startY + shown.length * rowHeight + 46
  ctx.fillStyle = '#7a8496'
  ctx.font = '22px ui-sans-serif, system-ui, sans-serif'
  ctx.textAlign = 'left'
  ctx.fillText(
    tickets.length > shown.length
      ? `分享图展示前 ${shown.length} 注，共 ${tickets.length} 注`
      : '仅作号码组合记录，不代表提高中奖概率',
    72,
    footerY,
  )

  ctx.textAlign = 'right'
  ctx.fillText(
    new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date()),
    width - 72,
    footerY,
  )

  return canvas.toDataURL('image/png')
}

export function downloadShareImage(tickets: ShareTicket[], options: ShareOptions) {
  const anchor = document.createElement('a')
  anchor.href = createShareImage(tickets, options)
  anchor.download = `${options.filename}-${new Date().toISOString().slice(0, 10)}.png`
  anchor.click()
}
